import { Address, Patient } from '@medplum/fhirtypes';

/** The home address as the PN card edits it (BAR-3001): street, optional apt or suite, city, state, ZIP. */
export interface HomeAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
}

/** A two-letter state code, as `Address.state` holds it. */
export const US_STATE = /^[A-Z]{2}$/;
/** A 5-digit ZIP, or ZIP+4. */
export const US_ZIP = /^\d{5}(?:-\d{4})?$/;

/** Uses that never mean the patient's home: a billing, work, temporary or old address. */
const NOT_HOME: readonly Address['use'][] = ['billing', 'work', 'temp', 'old'];

/**
 * Where the home address sits in the list: the first entry marked home, else
 * the first with no use (signup's ZIP has none). Never a billing, work,
 * temporary or old address, which a chart can hold ahead of the home one. -1
 * when there's none.
 */
function homeIndex(addresses: Address[] | undefined): number {
  const list = addresses ?? [];
  const marked = list.findIndex((address) => address.use === 'home');
  return marked !== -1 ? marked : list.findIndex((address) => !NOT_HOME.includes(address.use));
}

/** The home address entry as stored, for readers of where the patient lives. */
export function homeAddressEntry(patient: Patient | undefined): Address | undefined {
  const index = homeIndex(patient?.address);
  return index === -1 ? undefined : patient?.address?.[index];
}

/**
 * Whether the home address is already the first entry, marked home, where
 * readers of `address[0]` find it.
 */
export function homeAddressIsFirst(patient: Patient | undefined): boolean {
  return homeIndex(patient?.address) === 0 && patient?.address?.[0]?.use === 'home';
}

/**
 * The home address as the card shows it. Empty strings where nothing is on
 * file, e.g. signup's ZIP alone.
 */
export function readHomeAddress(patient: Patient | undefined): HomeAddress {
  const address = homeAddressEntry(patient);
  return {
    line1: address?.line?.[0] ?? '',
    line2: address?.line?.[1] ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    postalCode: address?.postalCode ?? '',
  };
}

/** The value trimmed, with the state upper-cased. */
export function normalizeHomeAddress(value: HomeAddress): HomeAddress {
  return {
    line1: value.line1.trim(),
    line2: value.line2.trim(),
    city: value.city.trim(),
    state: value.state.trim().toUpperCase(),
    postalCode: value.postalCode.trim(),
  };
}

/**
 * Why the address can't be saved, or undefined. A street line is required:
 * the backend fills `Patient.address` from MySQL whenever Medplum has no street
 * line, so an address without one would be replaced on the next sync.
 */
export function homeAddressError(value: HomeAddress): string | undefined {
  const address = normalizeHomeAddress(value);
  if (!address.line1) return 'Enter the street and number';
  if (!address.city) return 'Enter the city';
  if (!US_STATE.test(address.state)) return 'Enter the state as two letters';
  if (!US_ZIP.test(address.postalCode)) return 'Enter a 5-digit ZIP';
  return undefined;
}

/**
 * The address list with the home address replaced and moved to the front,
 * where every reader takes `address[0]` (DoseSpot, call routing, eligibility).
 * Rebuilt from whichever copy it's applied to, so a retry keeps any other
 * address. Only the entry's id and country carry over: district, period or a
 * third line from the old address would not describe the new one.
 */
export function withHomeAddress(addresses: Address[] | undefined, value: HomeAddress): Address[] {
  const address = normalizeHomeAddress(value);
  const list = addresses ?? [];
  const index = homeIndex(list);
  const current = index === -1 ? undefined : list[index];
  const home: Address = {
    ...(current?.id && { id: current.id }),
    use: 'home',
    line: [address.line1, address.line2].filter(Boolean),
    city: address.city,
    state: address.state,
    postalCode: address.postalCode,
    country: current?.country ?? 'US',
  };
  return [home, ...list.filter((_, i) => i !== index)];
}

/** Whether two home addresses are the same once normalized. */
export function sameHomeAddress(a: HomeAddress, b: HomeAddress): boolean {
  const x = normalizeHomeAddress(a);
  const y = normalizeHomeAddress(b);
  return (
    x.line1 === y.line1 &&
    x.line2 === y.line2 &&
    x.city === y.city &&
    x.state === y.state &&
    x.postalCode === y.postalCode
  );
}
